<template>
  <div
    class="seo-hero"
    :class="{ 'seo-hero--scene': scene }"
    :style="backgroundVars"
  >
    <div class="wrapper">
      <div class="intro-container">
        <h1 class="header">
          <slot />
        </h1>
      </div>
      <div class="trust-pilot-image"></div>
      <!-- A real anchor, not a button. The modal is the fast path for anyone
           whose JavaScript runs, but the href is what a crawler follows, what
           a middle-click opens and what a screen reader announces — and it now
           carries the bike type the visitor already told us by being here. -->
      <a
        :href="quoteHref"
        class="button ps-button is-inverted get-quote-bt"
        data-test="seo-hero-cta"
        @click="onCta"
      >
        Get your personal quote
      </a>
    </div>
    <!-- Animated scene, drawn client-side into a box whose aspect ratio is
         reserved here, so nothing below moves when it appears (no CLS). It
         is decorative: the H1 above stays the LCP element and the page's
         only statement of what it is about. -->
    <div
      v-if="scene"
      ref="scene"
      class="seo-hero__scene"
      :style="{ aspectRatio: `1200 / ${scene.height || 500}` }"
    />
  </div>
</template>

<script>
import { BIKE_TYPES, quotePath } from '@/helpers/estimator'

// Shared hero for the SEO landing pages (road / mountain / cargo / e-bike /
// commuter / triathlon). Each page used to carry its own copy of this markup
// and ~90 lines of near-identical scoped CSS; the only real difference was the
// pair of background illustrations, which now come in as props.
export default {
  name: 'SeoHero',
  props: {
    // Illustration anchored to the bottom-left of the hero. Required unless
    // the page passes a `scene` instead.
    leftImage: { type: String, default: '' },
    // Illustration anchored to the bottom-right.
    rightImage: { type: String, default: '' },
    // Horizontal nudge for the illustrations — the pages crop differently.
    leftPosition: { type: String, default: '-90px' },
    rightPosition: { type: String, default: '10px' },
    // Some pages shift the left illustration again on large screens.
    leftPositionDesktop: { type: String, default: '' },
    // Which bike this page is about. Drives the CTA's href so the intent the
    // visitor signalled by landing here survives the click. Validated against
    // the list /quote accepts, so a typo fails loudly in development rather
    // than shipping a parameter the destination silently ignores.
    bikeType: {
      type: String,
      required: true,
      validator: (value) => Object.keys(BIKE_TYPES).includes(value),
    },
    // A helpers/pedalsure-scene.js config. When set, an animated scene
    // replaces the two background illustrations.
    scene: { type: Object, default: null },
  },
  computed: {
    // Server-rendered, so it is in the raw HTML rather than applied on
    // hydration. `lp-` marks the landing-page funnel in analytics.
    quoteHref() {
      return quotePath({ bike: this.bikeType, src: `lp-${this.bikeType}` })
    },
    // Passed as custom properties because the images sit on ::before/::after,
    // which inline styles can't reach.
    backgroundVars() {
      if (!this.leftImage || !this.rightImage) {
        return {}
      }
      return {
        // Quoted: several illustration filenames contain spaces, and an
        // unquoted url() with a space is invalid CSS — the browser drops the
        // whole declaration and the hero renders with no artwork.
        '--hero-left': `url("${this.leftImage}")`,
        '--hero-right': `url("${this.rightImage}")`,
        '--hero-left-pos': this.leftPosition,
        '--hero-left-pos-lg': this.leftPositionDesktop || this.leftPosition,
        '--hero-right-pos': this.rightPosition,
      }
    },
  },
  mounted() {
    if (!this.scene) {
      return
    }
    // After the page is interactive, not during hydration: the engine and
    // its few hundred SVG nodes are pure decoration and shouldn't compete
    // with the CTA becoming clickable (TBT/INP). Its own chunk, so pages
    // without a scene never download it.
    const whenIdle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200))
    whenIdle(
      async () => {
        const { mount } = await import(
          /* webpackChunkName: "pedalsure-scene" */ '@/helpers/pedalsure-scene'
        )
        if (this.isDestroyed || !this.$refs.scene) {
          return
        }
        this.sceneInstance = mount(this.$refs.scene, {
          decorative: true,
          ...this.scene,
        })
      },
      { timeout: 2000 }
    )
  },
  beforeDestroy() {
    this.isDestroyed = true
    if (this.sceneInstance) {
      this.sceneInstance.destroy()
    }
  },
  methods: {
    // The modal stays the fast path, but only for a plain left click. A
    // modified click is the visitor deliberately asking for a new tab or
    // window, and swallowing it would strand them on the page they are
    // already on.
    onCta(event) {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return
      }
      event.preventDefault()
      this.$emit('cta')
    },
  },
}
</script>

<style lang="scss" scoped>
.seo-hero {
  position: relative;
  // Generous bottom padding keeps the headline clear of the illustrations,
  // which are anchored to the bottom of this box.
  padding: 32px 0 120px;

  @include tablet {
    padding: 56px 0 180px;
  }

  @include desktop {
    padding: 64px 0 240px;
  }

  &:before,
  &:after {
    content: '';
    position: absolute;
    height: 100%;
    width: 50%;
    bottom: 0;
    top: 0;
    z-index: -2;
    background-repeat: no-repeat;
    background-size: 170%;
    background-position-y: bottom !important;

    @include tablet {
      background-size: 140%;
    }

    @include desktop {
      background-size: 100%;
    }
  }

  &:before {
    background-image: var(--hero-left);
    left: 0;
    background-position: var(--hero-left-pos);

    @include desktop {
      background-position: var(--hero-left-pos-lg);
    }
  }

  &:after {
    background-image: var(--hero-right);
    right: 0;
    background-position: var(--hero-right-pos);
  }
}

// Scene variant: the illustration sits under the CTA instead of behind the
// headline, so the hero needs no bottom padding to keep them apart.
.seo-hero--scene {
  padding-bottom: 0;
  overflow: hidden;

  &:before,
  &:after {
    content: none;
  }
}

.seo-hero__scene {
  position: relative;
  max-width: 1400px;
  margin: 8px auto 0;
  // Fades the scene out at its edges, so on screens wider than 1400px it
  // doesn't end in a hard vertical cut.
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 6%,
    #000 94%,
    transparent
  );

  // Cropped to the middle of the scene on phones, where the full 1200-wide
  // scene would shrink the riders to a few pixels. The riders and the trail
  // sit in the central two-thirds, so nothing that matters is lost.
  @include mobile {
    width: 150%;
    margin-left: -25%;
  }
}

.wrapper {
  margin: 0 auto;
  max-width: 800px;
  // The CTA is inline-flex (shared button spec), so auto margins can't centre
  // it — the parent does.
  text-align: center;
  position: relative;
  z-index: 1;
}

.intro-container {
  padding: 0 15px;

  @include tablet {
    padding: 0;
  }
}

// Was sized in viewport units (9vw / 5.5vw / 4.2vw), so the H1 reached ~107px
// on a 2560px display. Now on the shared heading scale, capped at 56px.
.header {
  font-size: clamp(34px, 6vw, 56px);
  line-height: 1.08;
  font-weight: 700;
  letter-spacing: -0.025em;
  max-width: 15ch;
  text-align: center;
  color: $ps-primary;
  margin: 0 auto 24px;
}

.trust-pilot-image {
  text-align: center;
  margin-bottom: 10px;

  p {
    font-size: $ps-text-small;
  }

  @include tablet {
    margin-bottom: 30px;
  }

  img {
    width: 120px;
    height: auto;

    @include tablet {
      width: 160px;
    }
  }
}

.get-quote-bt {
  width: 300px;
  max-width: 100%;
}
</style>
